import {
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Switch,
  Divider,
  ListItemIcon,
  ListItemButton,
  Avatar,
  Stack,
  type ThemeOptions,
} from '@mui/material';
import {
  Palette,
  Brightness4,
  SystemUpdate,
  BrightnessAutoOutlined,
  LightMode,
  DarkMode,
} from '@mui/icons-material';
import { get } from 'lodash';
import { Controller } from 'react-hook-form';
import { type FC } from 'react';

import {
  type ThemeAppearance,
  type ThemeName,
} from '#apps/client-web/features/settings/models/app-settings';

import { type AppSettingsForm } from './types';

export type UserSettingsPageRendererProps = {
  form: AppSettingsForm;

  onSelectThemeDialog: (theme?: ThemeName) => void;
  onSelectThemeAppearanceDialog: (appearance?: ThemeAppearance) => void;

  themeOptions?: ThemeOptions;
};

export const UserSettingsPageRenderer: FC<UserSettingsPageRendererProps> = ({
  form,

  onSelectThemeDialog,
  onSelectThemeAppearanceDialog,

  themeOptions,
}) => {
  const { control } = form;

  return (
    <Stack sx={{ flexGrow: 1 }}>
      <List
        subheader={
          <ListSubheader component="div" sx={{ bgcolor: 'background.paper' }}>
            General
          </ListSubheader>
        }>
        <Controller
          control={control}
          name="autoUpdate.enabled"
          render={({ field }) => (
            <ListItem>
              <ListItemIcon>
                <SystemUpdate />
              </ListItemIcon>
              <ListItemText
                primary="Auto-update"
                secondary="Automatically install updates when available"
              />
              <Switch edge="end" {...field} checked={field.value} />
            </ListItem>
          )}
        />
      </List>

      <Divider />

      <List
        subheader={
          <ListSubheader component="div" sx={{ bgcolor: 'background.paper' }}>
            Theme
          </ListSubheader>
        }>
        <Controller
          control={control}
          name="theme.name"
          rules={{
            required: { value: true, message: 'Theme name is required' },
          }}
          render={({ field }) => (
            <ListItemButton
              onClick={onSelectThemeDialog.bind(undefined, field.value)}>
              <ListItemIcon>
                <Palette />
              </ListItemIcon>
              <ListItemText
                primary="Color"
                secondary="Choose your preferred theme"
              />
              <Avatar
                sx={{
                  bgcolor: get(themeOptions, ['palette', 'primary', 'main']),
                }}>
                &nbsp;
              </Avatar>
            </ListItemButton>
          )}
        />

        <Controller
          control={control}
          name="theme.appearance"
          rules={{
            required: {
              value: true,
              message: 'Theme appearance is required',
            },
          }}
          render={({ field }) => (
            <ListItemButton
              onClick={onSelectThemeAppearanceDialog.bind(
                undefined,
                field.value,
              )}>
              <ListItemIcon>
                <Brightness4 />
              </ListItemIcon>
              <ListItemText
                primary="Appearance"
                secondary="Choose how the app looks"
              />
              {field.value === 'system' && <BrightnessAutoOutlined />}
              {field.value === 'light' && <LightMode />}
              {field.value === 'dark' && <DarkMode />}
            </ListItemButton>
          )}
        />
      </List>
    </Stack>
  );
};
